import React, { useEffect, useState, useCallback, useRef } from "react";
import { searchChannels, getChannelVideos } from "../services/youtubeApi";
import { useMusicPlayer } from "../context/MusicPlayerContext";

const Artists = () => {
  const [artists, setArtists] = useState([]);
  const [artistVideos, setArtistVideos] = useState([]);
  const [selectedArtist, setSelectedArtist] = useState(null);
  const [searchTerm, setSearchTerm] = useState("popular Indian singers");
  const [loading, setLoading] = useState(false);
  const [loadingVideos, setLoadingVideos] = useState(false);
  const [error, setError] = useState("");
  const touchStartX = useRef(0);
  const touchEndX = useRef(0);

  const { playSong, currentSong, isPlaying, handleNext, handlePrevious, currentTime, duration } = useMusicPlayer();

  const handleTouchStart = (e) => {
    touchStartX.current = e.touches[0].clientX;
  };

  const handleTouchEnd = (e) => {
    touchEndX.current = e.changedTouches[0].clientX;
    const diff = touchStartX.current - touchEndX.current;
    const minSwipeDistance = 50;
    
    if (Math.abs(diff) > minSwipeDistance) {
      e.preventDefault();
      if (diff > 0) {
        console.log('Swipe left - calling handleNext');
        handleNext();
      } else {
        console.log('Swipe right - calling handlePrevious');
        handlePrevious();
      }
    }
  };

  const searchArtists = useCallback(async (term) => {
    if (!term?.trim()) {
      setArtists([]);
      return;
    }
    try {
      setLoading(true);
      setError("");
      const results = await searchChannels(term, 20);
      setArtists(results);
    } catch (err) {
      console.error("Error searching artists:", err);
      setError("Failed to search artists");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    searchArtists(searchTerm);
  }, [searchTerm, searchArtists]);

  const handleSearch = () => {
    searchArtists(searchTerm);
  };

  const handleArtistClick = async (artist) => {
    setSelectedArtist(artist);
    setLoadingVideos(true);
    try {
      const videos = await getChannelVideos(artist.id, 20);
      setArtistVideos(videos);
    } catch (err) {
      console.error("Error fetching artist videos:", err);
    } finally {
      setLoadingVideos(false);
    }
  };

  const handlePlayVideo = (video) => {
    playSong(video, artistVideos);
  };

  const handleBack = () => {
    setSelectedArtist(null);
    setArtistVideos([]);
  };

  return (
    <div 
      className="min-h-screen bg-gray-900 text-white pb-28"
      onTouchStart={handleTouchStart}
      onTouchEnd={handleTouchEnd}
    >
      {currentSong && (
        <div className="w-full h-1 bg-gray-600 rounded-full cursor-pointer">
          <div
            className="h-full bg-gradient-to-r from-green-500 to-green-400 rounded-full transition-all duration-150"
            style={{ width: `${Math.min(100, (currentTime / (duration || 1)) * 100)}%` }}
          />
        </div>
      )}
      {!selectedArtist ? (
        <>
          <div className="p-6 border-b border-gray-700">
            <h1 className="text-3xl font-bold text-purple-400">
              🎤 Artists
            </h1>
            <p className="text-gray-400">
              Search and discover artists on YouTube
            </p>
            <div className="mt-4 flex gap-3">
              <input
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
                placeholder="Search artists..."
                className="flex-1 px-4 py-3 rounded bg-gray-800 outline-none text-white placeholder-gray-400"
              />
              <button
                onClick={handleSearch}
                className="px-6 py-3 bg-purple-600 rounded font-semibold hover:bg-purple-700 transition"
              >
                Search
              </button>
            </div>
          </div>

          {error && <p className="text-red-400 text-center mt-4">{error}</p>}

          <div className="p-6">
            {loading ? (
              <div className="flex justify-center py-20">
                <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-purple-500"></div>
              </div>
            ) : (
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4">
                {artists.map((artist) => (
                  <div
                    key={artist.id}
                    onClick={() => handleArtistClick(artist)}
                    className="bg-gray-800 p-4 rounded-lg cursor-pointer hover:bg-gray-700 transition"
                  >
                    <img
                      src={artist.image}
                      alt={artist.name}
                      className="w-full aspect-square object-cover rounded-lg mb-3"
                    />
                    <h3 className="font-semibold text-sm truncate text-white">
                      {artist.name}
                    </h3>
                    <p className="text-xs text-gray-400 truncate">
                      {artist.description?.slice(0, 50) || "YouTube Artist"}
                    </p>
                  </div>
                ))}
              </div>
            )}
            {!loading && artists.length === 0 && (
              <p className="text-gray-400 text-center py-10">
                Search for artists to see results
              </p>
            )}
          </div>
        </>
      ) : (
        <div className="p-6">
          <button
            onClick={handleBack}
            className="flex items-center gap-2 text-purple-400 hover:text-purple-300 mb-4"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
            Back to Artists
          </button>

          <div className="flex items-center gap-4 mb-6">
            <img
              src={selectedArtist.image}
              alt={selectedArtist.name}
              className="w-20 h-20 rounded-full object-cover"
            />
            <div>
              <h2 className="text-2xl font-bold text-white">{selectedArtist.name}</h2>
              <p className="text-gray-400">YouTube Artist</p>
            </div>
          </div>

          <h3 className="text-xl font-semibold mb-4">Videos</h3>

          {loadingVideos ? (
            <div className="flex justify-center py-10">
              <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-purple-500"></div>
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
              {artistVideos.map((video) => (
                <div
                  key={video.id}
                  onClick={() => handlePlayVideo(video)}
                  className={`bg-gray-800 p-4 rounded-lg cursor-pointer hover:bg-gray-700 transition ${
                    currentSong?.id === video.id ? 'ring-2 ring-purple-500' : ''
                  }`}
                >
                  <div className="relative">
                    <img
                      src={video.artwork_url}
                      alt={video.title}
                      className="rounded mb-3 w-full h-40 object-cover"
                    />
                    <div className="absolute inset-0 flex items-center justify-center bg-black/30 opacity-0 hover:opacity-100 transition-opacity rounded-lg">
                      <div className={`w-12 h-12 rounded-full flex items-center justify-center ${
                        currentSong?.id === video.id && isPlaying
                          ? 'bg-purple-500'
                          : 'bg-purple-600'
                      }`}>
                        {currentSong?.id === video.id && isPlaying ? (
                          <svg className="w-6 h-6 text-white" fill="currentColor" viewBox="0 0 24 24">
                            <path d="M6 5h4v14H6zM14 5h4v14h-4z" />
                          </svg>
                        ) : (
                          <svg className="w-6 h-6 text-white ml-1" fill="currentColor" viewBox="0 0 24 24">
                            <path d="M8 5v14l11-7z" />
                          </svg>
                        )}
                      </div>
                    </div>
                  </div>
                  <h3 className="font-semibold text-sm truncate text-white">
                    {video.title}
                  </h3>
                  <p className="text-xs text-gray-400 truncate">
                    {video.singer}
                  </p>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default Artists;